import { authClient } from '../../utils/amplifyClient';
import { uploadData } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import { CreateEquipmentCategoryInput, UpdateEquipmentCategoryInput } from '../../API';
import type { CreateEquipmentCategoryInput as GqlCreateEquipmentCategoryInput } from '../../graphql/API';
import { EquipmentCategory } from './equipmentTypes';
import {
    createEquipmentCategory as createEquipmentCategoryMutation,
    updateEquipmentCategory as updateEquipmentCategoryMutation,
} from '../../graphql/mutations';
import { resizeFile } from '../../utils/helperFunctions';
import { S3_IMAGE_PATH } from '../../utils/constants';


export interface SaveNewEquipmentCategoryInput {
    companyId: string;
    name: string;
    /** Optional; omitted when creating from the category form (assign form on the unit instead). */
    formId?: string | null;
    imageFile?: File | null;
}

export interface UpdateEquipmentCategoryInputParams {
    companyId: string;
    categoryId: string;
    name: string;
    /** Optional; when omitted, existing formId is left unchanged. */
    formId?: string | null;
    imageFile?: File | null;
    imageRemoved?: boolean;
}

const uploadCategoryImage = async (companyId: string, name: string, imageFile: File): Promise<string> => {
    const resizedImage = await resizeFile(imageFile);
    const image = resizedImage as File;
    const fileName = `${uuidv4()}-IMAGE-${name.trim()}-${image.name}`;
    const savedImage = await uploadData({
        path: S3_IMAGE_PATH.fullEquipmentImagePath(companyId, fileName),
        data: image,
    }).result;

    return savedImage.path;
};

export async function saveNewEquipmentCategory({
    companyId,
    name,
    formId,
    imageFile,
}: SaveNewEquipmentCategoryInput): Promise<EquipmentCategory> {
    const imagePath = imageFile ? await uploadCategoryImage(companyId, name, imageFile) : undefined;

    const input: CreateEquipmentCategoryInput = {
        name: name.trim(),
        companyId,
        ...(formId ? { formId } : {}),
        ...(imagePath ? { image: imagePath } : {}),
    };

    // The deployed schema (amplify/data/resource.ts) marks EquipmentCategory.formId
    // optional (a.string()), but the generated graphql/API codegen types it as required.
    // Bridge that stale drift here without altering the runtime payload.
    const response = (await authClient.graphql({
        query: createEquipmentCategoryMutation,
        variables: { input: input as GqlCreateEquipmentCategoryInput },
    })) as { data?: { createEquipmentCategory?: EquipmentCategory | null }; errors?: Array<{ message?: string }> };

    const created = response.data?.createEquipmentCategory;
    if (!created?.id) {
        const message = response.errors?.[0]?.message || 'Create equipment category returned no id';
        throw new Error(message);
    }

    return created;
}

export async function updateEquipmentCategory({
    companyId,
    categoryId,
    name,
    formId,
    imageFile,
    imageRemoved = false,
}: UpdateEquipmentCategoryInputParams): Promise<EquipmentCategory> {
    const imagePath = imageFile ? await uploadCategoryImage(companyId, name, imageFile) : undefined;

    const input: UpdateEquipmentCategoryInput = {
        id: categoryId,
        name: name.trim(),
    };

    if (formId !== undefined) {
        input.formId = formId || null;
    }

    if (imagePath) {
        input.image = imagePath;
    } else if (imageRemoved) {
        input.image = '';
    }

    const response = (await authClient.graphql({
        query: updateEquipmentCategoryMutation,
        variables: { input },
    })) as { data?: { updateEquipmentCategory?: EquipmentCategory | null }; errors?: Array<{ message?: string }> };

    const updated = response.data?.updateEquipmentCategory;
    if (!updated?.id) {
        const message = response.errors?.[0]?.message || 'Update equipment category returned no id';
        throw new Error(message);
    }

    return updated;
}
